import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { SchedoraLogo } from "@/components/schedora/SchedoraLogo";
import { useApp } from "@/context/AppContext";

export default function Login() {
  const navigate = useNavigate();
  const { login, configError } = useApp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("Enter both your email and password.");
      return;
    }

    setIsSubmitting(true);
    const result = await login(email, password);

    if (!result.ok) {
      setError(result.message ?? "Unable to sign in.");
      setIsSubmitting(false);
      return;
    }

    if (!result.role) {
      setError("Your account signed in, but your workspace is still syncing. Please wait a moment and try again.");
      setIsSubmitting(false);
      return;
    }

    navigate(
      result.role === "owner"
        ? "/owner/dashboard"
        : result.role === "staff"
          ? "/staff/dashboard"
        : result.role === "super_admin"
          ? "/admin/dashboard"
          : "/customer/dashboard",
    );
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 gradient-hero items-center justify-center p-12">
        <div className="max-w-md text-center">
          <SchedoraLogo iconOnly iconClassName="h-12 w-12 mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold text-foreground">Welcome back to Schedora</h2>
          <p className="mt-3 text-muted-foreground">Your beauty business command center awaits.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <SchedoraLogo textClassName="text-xl" />
          </Link>
          <h1 className="text-2xl font-heading font-bold text-foreground">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Enter your credentials to continue</p>
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            {configError && <p className="text-xs text-muted-foreground">{configError}</p>}
            <Button className="w-full" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/signup" className="text-primary font-medium hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
